"use client";
import { useEffect, useId, useRef, useState } from "react";
import { ChevronIcon } from "@soma/ui";
import { Tag } from "@/components/ui/Tag";
import { cn } from "@/lib/utils";

export interface SelectOption {
  value: string;
  label: string;
  /** A second, quieter line - "Current", "Archive" and the like. */
  hint?: string;
  /** The running one: its hint becomes a green badge, so it stands out in a long list. */
  live?: boolean;
}

function Hint({ option }: { option: SelectOption }) {
  if (!option.hint) return null;
  // Same case and tracking as the badge, so the column of hints reads as one set.
  if (!option.live) {
    return <span className="font-mono text-[9.5px] tracking-[0.08em] text-fg-faint uppercase">{option.hint}</span>;
  }
  return (
    <Tag tone="ok" size="xs">
      {option.hint}
    </Tag>
  );
}

/**
 * A listbox we own. The native <select> cannot be styled past its border: the popup is
 * drawn by the OS, so it arrives in the system font at the system size with no room for
 * the hint that says which competition is the live one - a control that looks borrowed
 * from another product in the middle of the dashboard.
 *
 * Keyboard behaviour follows the ARIA listbox pattern rather than being reinvented:
 * arrows move a highlight, Enter or Space commits it, Escape abandons it, and focus
 * returns to the trigger either way.
 */
export function Select({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  label: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);

  const selected = options.findIndex((o) => o.value === value);
  const current = options[selected] ?? options[0];

  // Opening starts on the current choice, not at the top: the list can be long and the
  // thing someone is most likely to move away from is where they are.
  function openList() {
    setActive(selected >= 0 ? selected : 0);
    setOpen(true);
  }

  function commit(index: number) {
    const option = options[index];
    setOpen(false);
    trigger.current?.focus();
    if (option && option.value !== value) onChange(option.value);
  }

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [open]);

  useEffect(() => {
    if (open) list.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    list.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  function onKeyDown(e: React.KeyboardEvent) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((i) => Math.min(i + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        e.preventDefault();
        setActive(0);
        break;
      case "End":
        e.preventDefault();
        setActive(options.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        commit(active);
        break;
      case "Escape":
      case "Tab":
        setOpen(false);
        trigger.current?.focus();
        break;
    }
  }

  if (!current) return null;

  return (
    <div ref={wrap} className={cn("relative", className)}>
      <button
        ref={trigger}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${current.label}`}
        onClick={() => (open ? setOpen(false) : openList())}
        className={cn(
          // Opaque, so it reads as a control wherever it sits - over the contour rings too.
          "flex h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-full border bg-bg px-4 text-[14px] transition-colors",
          open ? "border-fg-muted" : "border-line hover:border-fg-muted",
        )}
      >
        <span className="truncate">{current.label}</span>
        <span className="flex shrink-0 items-center gap-1.5">
          <Hint option={current} />
          <ChevronIcon className={cn("size-4 text-fg-muted transition-transform duration-200", open && "rotate-180")} />
        </span>
      </button>

      {open && (
        <ul
          ref={list}
          id={listId}
          role="listbox"
          aria-label={label}
          aria-activedescendant={`${listId}-${active}`}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className="absolute top-full right-0 z-50 mt-2 max-h-72 min-w-full overflow-y-auto rounded-xl border border-line bg-bg p-1.5 shadow-[0_16px_40px_-12px_rgb(0_0_0/0.18)] outline-none"
        >
          {options.map((option, i) => (
            <li
              key={option.value}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={option.value === value}
              data-active={i === active}
              onPointerEnter={() => setActive(i)}
              onClick={() => commit(i)}
              className={cn(
                "flex cursor-pointer items-center justify-between gap-6 rounded-lg px-3 py-2 text-[14px] whitespace-nowrap transition-colors",
                i === active ? "bg-bg-sunk text-fg" : "text-fg-muted",
              )}
            >
              <span className={cn((option.value === value || option.live) && "font-medium text-fg")}>
                {option.label}
              </span>
              <Hint option={option} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
